<script setup>
import { CheckIcon, FilterIcon, LockIcon, ShieldAlertIcon } from "lucide-vue-next";
import { computed, inject, nextTick, onMounted, ref, watchEffect } from "vue";

import InputError from "@/Components/Form/InputError.vue";
import SimpleLabel from "@/Components/Form/SimpleLabel.vue";
import SimpleDescription from "@/Components/SimpleDescription.vue";

const emit = defineEmits(["update:modelValue", "enter"]);

const props = defineProps({
  label: {
    type: String,
    required: false
  },
  classes: {
    type: String,
    required: false
  },
  labelClass: {
    type: String,
    required: false
  },
  description: {
    type: String,
    required: false
  },
  maxLength: {
    type: [Number, String],
    required: false,
    default: 64
  },
  regex: {
    type: Array,
    required: false,
    validator: (v) => v.length === 2 && v[0] instanceof RegExp && typeof v[1] === "string"
  },
  iconClass: {
    type: String,
    required: false
  },
  inputDefaultClass: {
    type: String,
    required: false,
    default: "form-input w-full border-2 h-12 pr-8"
  },
  inputClass: {
    type: String,
    default: "",
    required: false
  },
  wrapperClass: {
    type: String,
    default: "",
    required: false
  },
  error: {
    type: String,
    required: false
  },
  modelValue: {
    type: [String, Number],
    required: false
  },
  value: {
    type: [String, Number],
    required: false
  },
  type: {
    type: String,
    required: false,
    default: "text"
  },
  placeholder: {
    type: String,
    default: "",
    required: false
  },
  tooltip: {
    type: String,
    required: false
  },
  disabled: {
    type: Boolean,
    required: false
  },
  optional: {
    type: Boolean,
    required: false,
    default: false
  },
  required: {
    type: Boolean,
    required: false,
    default: true
  },
  autoIconWidth: {
    type: Boolean,
    required: false,
    default: false
  },
  identifier: {
    type: [String, Number, Boolean],
    required: false
  }
});

const twMerge = inject("twMerge");

const isDirty = ref(false);
const focusing = ref(false);
const vValue = ref(props.modelValue ?? props.value);
const isValueOverflow = ref(false);

const iconRef = ref(null);
const iconWidth = ref(0);
const iconMarginRight = 5;

const length = computed(() => {
  return Number(vValue.value?.length);
});

const computedIconClass = computed(() => twMerge("pointer-events-none pl-4 absolute inset-y-0 left-0 z-1 flex items-center", props.iconClass));

const computedClass = computed(() => twMerge(props.inputDefaultClass, props.inputClass));

const computedInputClass = computed(() =>
  twMerge(
    props.disabled
      ? "bg-50 dark:border-zinc-800 dark:bg-zinc-800/50"
      : "hover:border-500 focus:border-500 dark:border-200 dark:hover:border-zinc-500 dark:focus:border-zinc-500 dark:bg-zinc-900 dark:text-zinc-100",
    computedClass.value
  )
);

const focussing = (v) => {
  focusing.value = v;
};

const update = (e) => {
  if (!isDirty.value) {
    isDirty.value = true;
  }

  vValue.value = props.regex ? formatValue(e.target.value) : e.target.value;
  isValueOverflow.value = props.maxLength ? (vValue.value.length ?? 0) >= Number(props.maxLength) : false;
  emit("update:modelValue", vValue.value);
};

const enter = (v) => {
  emit("enter", { value: v?.target?.value, clear: clear });
};

const clear = () => {
  vValue.value = "";
  isDirty.value = false;
};

const formatValue = (val) => (val ? val.replace(props.regex[0], "") : "");

watchEffect(async () => {
  vValue.value = props.modelValue ?? props.value;

  if (props.identifier) {
    await nextTick();
    iconWidth.value = iconRef.value?.clientWidth ?? 0;
  }
});

onMounted(async () => {
  if (props.autoIconWidth) {
    iconWidth.value = iconRef.value?.clientWidth ?? 0;
  }
});
</script>

<template>
  <div :class="classes">
    <SimpleLabel v-if="label && !$slots.label" :required="required && !optional" :classes="labelClass" class="mb-2" :tooltip="tooltip">
      {{ label }}
    </SimpleLabel>
    <slot v-if="$slots.label" name="label" />

    <div class="flex" :class="wrapperClass">
      <slot name="prefix" :value="vValue" :clear="clear" />
      <!--end::prefix-->

      <div class="relative flex flex-grow items-stretch focus-within:z-1">
        <template v-if="$slots.icon">
          <div ref="iconRef" :class="computedIconClass">
            <slot name="icon" />
          </div>
        </template>
        <!--end::icon-->

        <input
          v-bind="$attrs"
          class="sizeable"
          :class="[
            error ? 'border-error' : 'border-300',
            $slots.icon ? 'pl-12' : '',
            computedInputClass,
            {
              peer: $slots.icon || $slots.suffix
            }
          ]"
          :placeholder="placeholder"
          :type="type"
          :maxLength="maxLength"
          :disabled="disabled"
          :value="vValue"
          :style="{ paddingLeft: $slots.icon && autoIconWidth ? iconWidth + iconMarginRight + 'px' : '' }"
          @keyup.enter="enter"
          @focus="focussing(true)"
          @blur="focussing(false)"
          @input="update"
        />

        <div
          v-if="!$slots.suffix && !disabled && isValueOverflow"
          v-tippy="{ content: maxLength + '자 이내로 입력해 주세요.', placement: 'top' }"
          class="absolute inset-y-0 right-3 z-1 flex items-center transition"
        >
          <ShieldAlertIcon class="h-4 w-4 text-red-600 dark:text-yellow-400" />
        </div>

        <div
          v-else-if="!$slots.suffix && !disabled && regex && !isValueOverflow"
          v-tippy="{ content: regex[1], placement: 'top' }"
          class="absolute inset-y-0 right-3 z-1 flex items-center transition"
        >
          <FilterIcon class="h-4 w-4 text-400" />
        </div>

        <div
          v-else-if="!$slots.suffix && !disabled && required"
          v-tippy="{ content: '필수 입력사항 입니다.', placement: 'top' }"
          class="absolute inset-y-0 right-3 z-1 flex items-center transition"
        >
          <CheckIcon class="h-4 w-4" :class="[length === 0 ? 'text-red-400 dark:text-yellow-400' : 'text-green-400']" />
        </div>

        <div v-if="disabled" class="absolute inset-y-0 right-3 z-1 flex items-center transition">
          <LockIcon class="h-3.5 w-3.5 text-400" />
        </div>

        <template v-if="$slots.suffix">
          <!--end::suffix-->
          <div class="absolute inset-y-0 right-0 z-1 flex items-center pr-2">
            <slot name="suffix" :value="vValue" :clear="clear" />
          </div>
        </template>
        <!--end::icon-->
      </div>
    </div>

    <div v-if="error" class="relative mt-1.5">
      <transition name="fade">
        <InputError :message="error" />
      </transition>
    </div>

    <SimpleDescription v-if="description && !error">
      {{ description }}
    </SimpleDescription>
  </div>
</template>
